// DevSandbox — placeholder page for the 10 locked games whose React
// engine isn't built yet (Dark Launch sprint).
//
// Mounted as a fallback route for /games/:gameId that didn't match
// any of the 5 specific live-game routes. The wrapper (see App.tsx —
// DevSandboxRoute) guards the mount: a player without a dev-pin grant
// for this game is redirected back to / before this component ever
// renders. That means by the time we get here, the user has either:
//   (a) Just verified the universal PIN for this game, or
//   (b) Still has an unexpired 1-hour dev grant from an earlier visit.
//
// The page itself is intentionally lightweight — no game engine, no
// WebSocket, just a "you're in, engine pending" message so the dev
// can confirm the unlock path works end-to-end.
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '@baaligames/shared-ui/components/Icon';
import { useLobbyGames } from '../../lib/lobbyGames';

export default function DevSandbox() {
  const { gameId } = useParams<{ gameId: string }>();
  const nav = useNavigate();
  const { data } = useLobbyGames();
  const game = data?.games.find((g) => g.id === gameId);

  // Cosmetic only — DevSandboxRoute already gated the mount.
  const displayName = game?.displayName || gameId || 'Game';
  const gradient = game?.themeGradient || 'from-violet-600 to-fuchsia-800';
  const emoji = game?.themeEmoji || '🛠️';

  return (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center px-6 py-10
                    bg-gradient-to-b from-arena-50 to-white
                    dark:from-arena-900 dark:to-arena-950">
      <div className="w-full max-w-md text-center space-y-5">
        {/* Big gradient tile with the game's emoji — same visual
            identity as the lobby card so the dev recognises the tile
            they just unlocked. */}
        <div
          className={`mx-auto w-28 h-28 rounded-3xl grid place-items-center
                      bg-gradient-to-br ${gradient}
                      shadow-2xl ring-1 ring-inset ring-white/15`}
        >
          {game?.logoPath ? (
            <img
              src={game.logoPath}
              alt=""
              className="w-full h-full object-cover rounded-3xl"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            />
          ) : (
            <span className="text-6xl drop-shadow" aria-hidden>{emoji}</span>
          )}
        </div>

        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-[.18em]
                          bg-emerald-100 dark:bg-emerald-500/15
                          text-emerald-700 dark:text-emerald-300
                          ring-1 ring-inset ring-emerald-300/40 dark:ring-emerald-400/30">
            <Icon name="bolt" size={12} filled />
            Developer Access Granted
          </div>
          <h1 className="mt-3 text-3xl font-extrabold text-arena-900 dark:text-white">
            {displayName}
          </h1>
          <p className="mt-2 text-[13.5px] text-arena-600 dark:text-neon-200/75 leading-relaxed">
            Engine under construction. This sandbox confirms your developer
            unlock works end-to-end — the gameplay code lands here once
            the game ships.
          </p>
        </div>

        <div className="rounded-2xl px-4 py-3 text-[12px] text-left
                        bg-white/70 dark:bg-arena-800/60
                        ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                        text-arena-700 dark:text-neon-200/75
                        backdrop-blur">
          <div className="font-bold mb-1 flex items-center gap-1.5">
            <Icon name="info" size={14} /> What you can test
          </div>
          <ul className="list-disc list-inside space-y-0.5 leading-snug">
            <li>Lock toggle → locked card on the lobby</li>
            <li>5-tap easter egg → PIN modal opens</li>
            <li>PIN verify → 1-hour grant + route here</li>
            <li>Manual URL nav while no grant → redirected home</li>
          </ul>
        </div>

        <button
          type="button"
          onClick={() => nav('/', { replace: true })}
          className="inline-flex items-center justify-center gap-1.5
                     px-5 py-2.5 rounded-full text-sm font-bold
                     text-white
                     bg-gradient-to-b from-violet-500 to-violet-700
                     hover:from-violet-400 hover:to-violet-600
                     shadow-[0_4px_14px_rgba(139,92,246,0.35)]
                     active:scale-[.98] transition"
        >
          <Icon name="arrow_back" size={16} /> Back to lobby
        </button>
      </div>
    </div>
  );
}
